import React from 'react';
import {
  ChatBubbleLeftRightIcon,
  GlobeEuropeAfricaIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  XMarkIcon,
  BoltIcon,
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon
} from '@heroicons/react/24/outline';

export default function AIAssistantHelp() {
  return (
    <div className="max-w-5xl mx-auto overflow-hidden rounded-[32px] border border-sky-100 bg-gradient-to-br from-sky-50 via-white to-indigo-50 shadow-xl">
      <div className="relative overflow-hidden bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 px-6 py-10 text-white sm:px-8 sm:py-12">
        <div className="absolute -left-12 top-0 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute right-0 top-0 h-48 w-48 rounded-full bg-cyan-300/20 blur-3xl" />
        <div className="relative z-10 text-center">
          <GlobeEuropeAfricaIcon className="mx-auto h-16 w-16 mb-4" />
          <h1 className="text-3xl font-extrabold sm:text-4xl">AI Assistant</h1>
          <p className="mt-3 text-base text-sky-100 sm:text-lg">
            A live company snapshot chat bot available from Dashboard Support.
          </p>
        </div>
      </div>

      <div className="space-y-6 p-6 sm:p-8">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="rounded-3xl bg-white p-6 shadow-md ring-1 ring-sky-100">
            <h2 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
              <BoltIcon className="h-6 w-6 text-sky-600" />
              What It Does
            </h2>
            <p className="mt-4 text-gray-700">
              The AI Assistant gives staff live information about their company directly inside InsightOS. It is designed for quick operational questions, such as what is happening today, what needs attention next, and which updates matter right now.
            </p>
            <div className="mt-5 rounded-2xl border-l-4 border-sky-500 bg-sky-50 p-4">
              <p className="text-sm font-medium text-sky-900">
                Use it for real-time answers about tasks, meetings, shifts, holiday status, bottlenecks, team activity, and company news.
              </p>
            </div>
          </div>

          <div className="rounded-3xl bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 p-5 text-slate-100 shadow-lg">
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-300">Example</p>
            <div className="mt-4 space-y-3 font-mono text-xs sm:text-sm">
              <div className="flex justify-end">
                <div className="max-w-xs rounded-2xl bg-sky-500/15 px-4 py-3 ring-1 ring-sky-400/20">
                  What is my next priority?
                </div>
              </div>
              <div className="rounded-2xl bg-white/5 px-4 py-3 ring-1 ring-white/10">
                <p><span className="text-sky-300">AI:</span> Your next priority is the care plan review due before 11:00.</p>
                <p className="mt-2"><span className="text-sky-300">AI:</span> You also have a meeting booked for 14:00 and one unread company update in News Feed.</p>
              </div>
            </div>
        </div>

          <div className="rounded-3xl bg-white p-6 shadow-md ring-1 ring-sky-100">
            <h2 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-sky-600" />
              Where To Find It
            </h2>
            <div className="mt-5 grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-sky-100 bg-sky-50 p-5">
                <div className="flex items-center gap-2 text-sky-950">
                  <ComputerDesktopIcon className="h-5 w-5 text-sky-600" />
                  <h3 className="text-lg font-semibold">Desktop</h3>
                </div>
                <p className="mt-3 text-sm text-sky-900">
                  Open <strong>Dashboard → Support</strong> and use the blue globe in the <strong>right-hand corner</strong>.
                </p>
              </div>
              <div className="rounded-2xl border border-indigo-100 bg-indigo-50 p-5">
                <div className="flex items-center gap-2 text-indigo-950">
                  <DevicePhoneMobileIcon className="h-5 w-5 text-indigo-600" />
                  <h3 className="text-lg font-semibold">Mobile</h3>
                </div>
                <p className="mt-3 text-sm text-indigo-900">
                  Open <strong>Dashboard → Support</strong> and use the blue globe from the <strong>top bar</strong>.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {[
              {
                icon: BuildingOffice2Icon,
                title: 'Live Company Data',
                body: 'Answers are based on the live state of your company inside InsightOS, not just static help text.',
                tone: 'text-sky-700 bg-sky-50 border-sky-100',
              },
              {
                icon: SparklesIcon,
                title: 'Quick Questions',
                body: 'Ask about your day, priorities, meetings, holidays, tasks, team activity, or company updates.',
                tone: 'text-violet-700 bg-violet-50 border-violet-100',
              },
              {
                icon: ChatBubbleLeftRightIcon,
                title: 'Simple UI',
                body: 'Use the preset prompts or open the assistant and work from the blue globe entry point.',
                tone: 'text-indigo-700 bg-indigo-50 border-indigo-100',
              },
              {
                icon: ExclamationTriangleIcon,
                title: 'Escalation',
                body: 'If the assistant cannot solve the issue, move into the wider Support area for further help.',
                tone: 'text-amber-700 bg-amber-50 border-amber-100',
              },
            ].map(({ icon: Icon, title, body, tone }) => (
              <div key={title} className={`rounded-3xl border p-5 shadow-sm ${tone}`}>
                <Icon className="h-6 w-6" />
                <h3 className="mt-4 text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-sm leading-6">{body}</p>
              </div>
            ))}
          </div>

          <div className="rounded-3xl bg-white p-6 shadow-md ring-1 ring-sky-100">
            <h2 className="flex items-center gap-2 text-2xl font-semibold text-gray-900">
              <PaperAirplaneIcon className="h-6 w-6 text-sky-600" />
              Suggested Questions
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                'What’s my day look like?',
                'My next priority?',
                'Today’s meetings?',
                'My tasks and deadlines?',
                'Holiday and absence status?',
                'Company news?',
              ].map((question) => (
                <div key={question} className="rounded-2xl border border-gray-200 bg-slate-50 px-4 py-3 text-sm font-medium text-gray-800">
                  {question}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border-l-4 border-amber-400 bg-amber-50 p-5">
            <div className="flex gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-500" />
              <p className="text-sm text-amber-900">
                <strong>Important:</strong> The AI Assistant is available from the <strong>Dashboard Support</strong> area. It is intended to surface live company information quickly so staff can get answers without leaving their workflow.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}